import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { fmtWh } from '../time';
import OrderDetailPanel from '../OrderDetailPanel';
import PacketaHandover, { shipDay } from './PacketaHandover';
import { guidedText } from './guidedText';
import type { PacketaClock } from './StationApp';

// Orders → Packeta (Fernando 3.11: "The Packeta handovers should work like orders that can be opened and added to. We
// should see what went with which order."). Every handover — one per Packeta pickup day (backend/ship_bulks.py) — with
// its parcels (who scanned which order, when), what was written on it as not scanned, and its record. The open one
// takes more parcels here too ("Add parcels": the same scan screen as the station).

interface Row {
  id: number; code: string; ship_date: string | null; status: string; opened_by: string | null; opened_at: string;
  closed_by: string | null; closed_at: string | null; note: string | null; parcels: number; not_scanned: number; late: boolean;
}
interface Detail {
  bulk: Row & { meta: any };
  items: { id: number; order_id: string; order_number: string; country_code: string | null; by_person: string; device: string | null; at: string }[];
  events: { kind: string; ok: boolean | null; reason: string | null; by_person: string | null; device: string | null; at: string; order_number: string | null; code: string | null }[];
  not_scanned: string[];
  missing: { order_id: string; order_number: string; processed_at: string }[];
}

const STATUS: Record<string, string> = { open: 'open — collecting', handed_over: 'handed over', closed: 'closed', cancelled: 'cancelled' };
const EVENT: Record<string, string> = { open: 'opened', start: 'started the handover', scan: 'scanned', remove: 'took out',
  close: 'courier took them', reopen: 'opened again', moved_day: 'moved to the next pickup' };
const utc = (iso: string | null) => (iso ? (iso.endsWith('Z') ? iso : `${iso}Z`) : '');

export function usePacketaClock(enabled: boolean): PacketaClock | null {
  const [c, setC] = useState<PacketaClock | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => authFetch(`${API}/bulks/packeta/clock`).then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setC(d); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return c;
}

export default function PacketaPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [clock, setClock] = useState<PacketaClock | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [d, setD] = useState<Detail | null>(null);
  const [adding, setAdding] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/bulks/packeta/list`);
    if (!r.ok) { setErr('Could not load the Packeta handovers'); return; }
    const j = await r.json();
    setRows(j.handovers || []); setClock(j.clock || null); setErr('');
  }, []);
  useEffect(() => { load(); }, [load]);
  const open = useCallback(async (id: number) => {
    setSel(id); setD(null);
    const r = await authFetch(`${API}/bulks/${id}`);
    if (r.ok) setD(await r.json());
  }, []);

  if (adding) {
    return (
      <div className="p-6 max-w-5xl mx-auto text-gray-200">
        <PacketaHandover tx={guidedText('en')} onBack={() => { setAdding(false); load(); if (sel) open(sel); }} />
      </div>
    );
  }
  const openRow = rows.find(r => r.status === 'open');
  return (
    <div className="p-6 max-w-6xl mx-auto text-gray-200">
      {orderId && <OrderDetailPanel orderId={orderId} lookOnly onClose={() => setOrderId(null)} onOpenOrder={id => setOrderId(id)} />}
      <div className="flex flex-wrap items-baseline gap-4 mb-1">
        <h1 className="text-2xl font-bold text-white">Packeta handovers</h1>
        <span className="text-gray-500 text-sm">one per pickup day — open until the courier takes it</span>
        <button onClick={() => setAdding(true)} className="ml-auto px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold">
          {openRow ? `Add parcels to ${openRow.code}` : 'Start a handover'}</button>
      </div>
      {clock && (
        <div className="mb-4 text-sm text-gray-400">
          {clock.pickup_at ? `Packeta picks up today at ${fmtWh(clock.pickup_at, 'time')}` : 'No Packeta pickup today'}
          {clock.waiting ? ` · ${clock.waiting} processed Packeta parcel${clock.waiting === 1 ? '' : 's'} not in a handover yet` : ''}
          {clock.handed_over_today ? ` · today's ${clock.handed_over_today.code} handed over by ${clock.handed_over_today.by} ${fmtWh(clock.handed_over_today.at, 'time')}` : ''}
        </div>
      )}
      {err && <div className="text-red-400 mb-3">{err}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          {rows.map(r => (
            <button key={r.id} onClick={() => open(r.id)}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${sel === r.id ? 'border-blue-500 bg-blue-950/30' : 'border-gray-800 bg-[#1a1d27] hover:border-gray-600'}`}>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-white font-semibold">{r.code}</span>
                <span className="text-gray-300">for {shipDay(r.ship_date)}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'open' ? 'bg-amber-700 text-white' : 'bg-gray-800 text-gray-300'}`}>{STATUS[r.status] || r.status}{r.late ? ' (said afterwards)' : ''}</span>
                <span className="ml-auto text-white font-bold">{r.parcels}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                opened by {r.opened_by || '—'} {fmtWh(utc(r.opened_at), 'datetime')}
                {r.closed_at ? ` · taken ${fmtWh(utc(r.closed_at), 'datetime')} (${r.closed_by})` : ''}
                {r.not_scanned ? ` · ${r.not_scanned} went without a scan` : ''}
              </div>
            </button>
          ))}
          {rows.length === 0 && <div className="text-gray-500 text-sm">No Packeta handover yet.</div>}
        </div>
        <div>
          {sel && !d && <div className="text-gray-500">Loading…</div>}
          {d && (
            <div className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4 space-y-4">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-xl font-bold text-white">{d.bulk.code}</span>
                <span className="text-gray-300">for {shipDay(d.bulk.ship_date)}</span>
                <span className="text-gray-400">{STATUS[d.bulk.status] || d.bulk.status} · {d.items.length} parcels</span>
                {d.bulk.status === 'open' && <button onClick={() => setAdding(true)} className="ml-auto px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm">Add parcels</button>}
              </div>
              {d.bulk.note && <div className="text-sm text-gray-300">{d.bulk.note}</div>}
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Parcels — click an order to open it</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-sm">
                  {d.items.map(i => (
                    <button key={i.id} onClick={() => setOrderId(i.order_id)} className="text-left rounded px-2 py-1 hover:bg-gray-800">
                      <span className="text-blue-300">{i.order_number}</span> <span className="text-gray-500">{i.by_person} {fmtWh(utc(i.at), 'time')}</span>
                    </button>
                  ))}
                </div>
              </div>
              {d.not_scanned.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Went without a scan ({d.not_scanned.length})</div>
                  <div className="text-sm text-gray-300">{d.not_scanned.join(', ')}</div>
                </div>
              )}
              {d.missing.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Processed, not scanned yet ({d.missing.length})</div>
                  <div className="text-sm text-gray-300">{d.missing.map(m => m.order_number).join(', ')}</div>
                </div>
              )}
              <details>
                <summary className="text-sm text-gray-400 cursor-pointer">The record ({d.events.length} steps)</summary>
                <div className="mt-2 text-xs text-gray-400 space-y-0.5">
                  {d.events.map((e, k) => (
                    <div key={k} className={e.ok === false ? 'text-red-300' : ''}>
                      {fmtWh(utc(e.at), 'datetime')} · {e.by_person || '—'}{e.device ? ` @ ${e.device}` : ''} · {EVENT[e.kind] || e.kind}
                      {e.order_number ? ` ${e.order_number}` : ''}{e.ok === false ? ' — refused' : ''}{e.reason ? ` — ${e.reason}` : ''}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
